import {
  ArrowDownWideNarrow,
  Hash,
  History,
  Layers,
  ListOrdered,
  type LucideIcon,
} from "lucide-react"

import { Skeleton } from "@/components/ui/alert"
import { cn } from "@/lib/utils"

import type { FindingId, Message } from "../../data/review-data"
import type { ReportFindingItem, ReportSectionItem, ReportSeverity } from "../report-data"

import { SegmentButton, Segmented } from "../segmented"

import { FindingRow } from "./finding-row"
import {
  arrangeFindings,
  groupBySection,
  presentSeverities,
  SEVERITY_FILTER_LABEL,
  type ListSort,
  type SeverityFilter,
} from "./list-data"

/** The filter's dot: the same red, amber and violet as the row's pill. */
const SEVERITY_DOT: Record<ReportSeverity, string> = {
  needs_sme: "bg-people",
  error: "bg-err",
  warn: "bg-print",
}

const SORTS: readonly { id: ListSort; label: string; Icon: LucideIcon }[] = [
  { id: "priority", label: "Priority", Icon: ListOrdered },
  { id: "section", label: "Section", Icon: Layers },
  { id: "number", label: "Position", Icon: Hash },
  { id: "occurrence", label: "Occurrence", Icon: ArrowDownWideNarrow },
]

export interface ListViewProps {
  /** The listed findings, in the server's priority order. */
  findings: readonly ReportFindingItem[]
  sections: readonly ReportSectionItem[]
  /** The outstanding report's rollup: which severity filters are offered. */
  severityCounts: Record<ReportSeverity, number>
  sort: ListSort
  onSortChange: (next: ListSort) => void
  filter: SeverityFilter
  onFilterChange: (next: SeverityFilter) => void
  showCorrected: boolean
  onShowCorrectedChange: (next: boolean) => void
  /** The corrected findings are on their way. */
  correctedLoading: boolean
  correctedFailed: boolean
  messagesFor: (findingId: FindingId) => readonly Message[]
  unreadFor: (findingId: FindingId) => number
  /** Omitted, with `onMarkRead`, for a reader who may only read the conversation. */
  onSendMessage?: ((findingId: FindingId, body: string) => void) | undefined
  onMarkRead?: ((findingId: FindingId) => void) | undefined
}

/**
 * Every finding at once, filtered by severity and arranged four ways. The
 * filter and sort live with the page, so they survive a trip to another tab.
 */
export function ListView({
  findings,
  sections,
  severityCounts,
  sort,
  onSortChange,
  filter,
  onFilterChange,
  showCorrected,
  onShowCorrectedChange,
  correctedLoading,
  correctedFailed,
  messagesFor,
  unreadFor,
  onSendMessage,
  onMarkRead,
}: ListViewProps): JSX.Element {
  const severities = presentSeverities(severityCounts)
  const flat = sort === "section" ? null : arrangeFindings(findings, sort, filter)
  const groups = sort === "section" ? groupBySection(findings, sections, filter) : null
  const empty = flat ? flat.length === 0 : (groups ?? []).length === 0

  const row = (f: ReportFindingItem): JSX.Element => (
    <FindingRow
      key={f.id}
      finding={f}
      thread={messagesFor(f.id)}
      unread={unreadFor(f.id)}
      onSend={
        onSendMessage
          ? (body) => {
              onSendMessage(f.id, body)
            }
          : undefined
      }
      onMarkRead={
        onMarkRead
          ? () => {
              onMarkRead(f.id)
            }
          : undefined
      }
    />
  )

  return (
    <div className="mx-auto flex max-w-[820px] flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-caption font-medium text-text-3">Show</span>
          <Segmented label="Show">
            <SegmentButton
              pressed={filter === "all"}
              onClick={() => {
                onFilterChange("all")
              }}
            >
              All
            </SegmentButton>
            {severities.map((severity) => (
              <SegmentButton
                key={severity}
                pressed={filter === severity}
                onClick={() => {
                  onFilterChange(severity)
                }}
              >
                <span
                  aria-hidden="true"
                  className={cn("size-2 rounded-full", SEVERITY_DOT[severity])}
                />
                {SEVERITY_FILTER_LABEL[severity]}
              </SegmentButton>
            ))}
          </Segmented>
          <Segmented label="Corrected findings">
            <SegmentButton
              pressed={showCorrected}
              onClick={() => {
                onShowCorrectedChange(!showCorrected)
              }}
            >
              <History className="size-3.5" aria-hidden="true" />
              Show corrected
            </SegmentButton>
          </Segmented>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-caption font-medium text-text-3">Sort by</span>
          <Segmented label="Sort by">
            {SORTS.map(({ id, label, Icon }) => (
              <SegmentButton
                key={id}
                pressed={sort === id}
                onClick={() => {
                  onSortChange(id)
                }}
              >
                <Icon className="size-3.5" aria-hidden="true" />
                {label}
              </SegmentButton>
            ))}
          </Segmented>
        </div>
      </div>

      {correctedLoading ? (
        <div aria-busy="true" className="flex flex-col gap-3">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-36 w-full rounded-lg" />
          ))}
          <span className="sr-only">Loading the corrected findings</span>
        </div>
      ) : correctedFailed ? (
        <p
          role="alert"
          className="rounded-lg border border-err/30 bg-err-tint p-4 text-center text-body-sm text-err"
        >
          The corrected findings could not be loaded.
        </p>
      ) : empty ? (
        <p className="rounded-lg border border-dashed border-border p-8 text-center text-body-sm text-text-3">
          Nothing in this filter.
        </p>
      ) : groups ? (
        <div className="flex flex-col gap-8">
          {groups.map((group) => (
            <section key={group.key} aria-label={group.title}>
              <h2 className="text-eyebrow uppercase text-text-3">{group.title}</h2>
              <div className="mt-3 flex flex-col gap-3">{group.findings.map(row)}</div>
            </section>
          ))}
        </div>
      ) : (
        <div className="flex flex-col gap-3">{(flat ?? []).map(row)}</div>
      )}
    </div>
  )
}
